import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { 
  CarouselWrapper,
  ArrowButton,
  CarouselInner,
  Item
} from "./styles";

interface CarouselProps {
  children: React.ReactNode[];
  showArrowsAtEdges?: boolean;
  buttonSize?: number;
  gap?: number;
  endSpacing?: number;
  padding?: number;
  centerWhenFewItems?: boolean;
}

export interface CarouselHandle {
  scrollToIndex: (index: number) => void;
}

export const Carousel = forwardRef<CarouselHandle, CarouselProps>(({
  children,
  showArrowsAtEdges = false,
  buttonSize = 25,
  gap = 2,
  endSpacing = -25,
  padding=30,
  centerWhenFewItems = false,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [maxTranslate, setMaxTranslate] = useState(0);
  const [showArrows, setShowArrows] = useState(true);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateMeasurements = () => {
    if (!containerRef.current || !innerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth - buttonSize * 2;
    const contentWidth = innerRef.current.scrollWidth;

    if (contentWidth <= containerWidth) {
      setShowArrows(false);
      setTranslateX(0);
      setMaxTranslate(0);
      setAtStart(true);
      setAtEnd(true);
    } else {
      setShowArrows(true);
      const maxT = -(contentWidth - containerWidth - endSpacing);
      setMaxTranslate(maxT);
      const clamped = Math.max(Math.min(translateX, 0), maxT);
      setTranslateX(clamped);
      setAtStart(clamped === 0);
      setAtEnd(clamped === maxT);
    }
  };

  useEffect(() => {
    updateMeasurements();
    window.addEventListener("resize", updateMeasurements);
    return () => window.removeEventListener("resize", updateMeasurements);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const scrollPrev = () => {
    if (!innerRef.current) return;
    const itemWidth = innerRef.current.children[0]?.clientWidth || 0;
    const move = itemWidth + gap;
    setTranslateX((prev) => {
      const next = Math.min(prev + move, 0);
      setAtStart(next === 0);
      setAtEnd(next === maxTranslate);
      return next;
    });
  };

  const scrollNext = () => {
    if (!innerRef.current) return;
    const itemWidth = innerRef.current.children[0]?.clientWidth || 0;
    const move = itemWidth + gap;
    setTranslateX((prev) => {
      const next = Math.max(prev - move, maxTranslate);
      setAtStart(next === 0);
      setAtEnd(next === maxTranslate);
      return next;
    });
  };

  const scrollToIndex = (index: number) => {
    if (!innerRef.current || !containerRef.current) return;
    
    const item = innerRef.current.children[index] as HTMLElement;
    if (!item) return;

    const containerWidth = containerRef.current.offsetWidth - buttonSize * 2;
    const contentWidth = innerRef.current.scrollWidth;
    const itemLeft = item.offsetLeft;
    const itemWidth = item.offsetWidth;
    
    if (contentWidth <= containerWidth) {
      setTranslateX(0);
      setAtStart(true);
      setAtEnd(true);
      return;
    }
    
    const currentMaxTranslate = -(contentWidth - containerWidth - endSpacing);

    let targetPosition: number;
    if (centerWhenFewItems && !showArrows) {
      targetPosition = -(itemLeft - containerWidth / 2 + itemWidth / 2);
    } else {
      targetPosition = -itemLeft + padding;
    }

    const clampedPosition = Math.max(Math.min(targetPosition, 0), currentMaxTranslate);
    
    setTranslateX(clampedPosition);
    setAtStart(clampedPosition === 0);
    setAtEnd(clampedPosition === currentMaxTranslate);
  };

  useImperativeHandle(ref, () => ({
    scrollToIndex,
  }));

  const shouldCenter = centerWhenFewItems && !showArrows;

  return (
    <CarouselWrapper ref={containerRef} showArrows={showArrows} padding={padding} centerWhenFewItems={shouldCenter}>
      <ArrowButton
        position="left"
        onClick={scrollPrev}
        visible={showArrows && (!showArrowsAtEdges || !atStart)}
        buttonSize={buttonSize}
        aria-label="Anterior"
      >
        <FaChevronLeft />
      </ArrowButton>

      <CarouselInner ref={innerRef} translateX={translateX} center={shouldCenter}>
        {children.map((child, idx) => (
          <Item key={idx} gap={gap}>
            {child}
          </Item>
        ))}
      </CarouselInner>

      <ArrowButton
        position="right"
        onClick={scrollNext}
        visible={showArrows && (!showArrowsAtEdges || !atEnd)}
        buttonSize={buttonSize}
        aria-label="Próximo"
      >
        <FaChevronRight />
      </ArrowButton>
    </CarouselWrapper>
  );
});

Carousel.displayName = 'Carousel';
